import {
  HOST_QUERY_KEY,
  metaApiInstance,
  QUERY_KEY,
  type ServiceAppState,
  TOKEN_SCOPE,
} from '@kc-console/context';
import { getConsoleBaseUrl, STATUS_CODE } from '@kc-console/core';

import { commonGuardErrorHandler } from '../../utils/commonGuardErrorHandler';
import { getRequestUrlData } from '../../utils/getRequestUrlData';
import { getServerTraceHttpTimingOptions } from '../../utils/serverTrace/store';

import type { Guard } from '../../types';

/**
 * 프로젝트 스콥으로 접근 시 프로젝트 id를 셋팅합니다.
 * @description
 * 1. 접근 가능하 project가 없는 경우, dashboard/welcome으로 리다이렉트합니다.
 * 2. query string에 projectId가 있고, 접근 가능한 경우 해당 프로젝트 id를 사용합니다.
 * - query string에 projectId가 없을 경우 최근 접근한 프로젝트 리스트 중 접근 가능한 첫 번째 프로젝트 id를 사용합니다.
 * - 최근 접근한 프로젝트 리스트가 없거나 접근 가능한 최근 접근한 프로젝트가 없을 경우 defaultProjectId를 사용합니다.
 * - defaultProjectId가 없을 경우 사용자가 접근 가능한 프로젝트 중 첫 번째 프로젝트 id를 사용합니다.
 */
export const determineProjectGuard: Guard<ServiceAppState> = async ({
  queryClient,
  state,
  context,
}) => {
  const { scope, token, defaultProjectId, accessibleProjects } = state;
  // project scope 서비스가 아니라면 필요없는 로직이므로 바로 가드 종료
  if (scope !== TOKEN_SCOPE.PROJECT) return { status: STATUS_CODE.OK };
  const { fetchRecentProjects } = metaApiInstance({
    token,
    ...getServerTraceHttpTimingOptions(context.req, 'config'),
  });
  const { projectId: queryProjectId, urlObj } = getRequestUrlData(context);
  try {
    const recentProjectIds = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_RECENT_PROJECT],
      queryFn: fetchRecentProjects,
    });

    const accessibleProjectIds = accessibleProjects.map(({ id }) => id) || [];
    const hasAccessibleProjects = !!accessibleProjectIds[0];
    const isAccessible = (id: string) => accessibleProjectIds.includes(id);

    // 접근 가능한 프로젝트가 없는 경우 dashboard/welcome으로 리다이렉트한다.
    if (!hasAccessibleProjects) {
      return {
        redirect: {
          destination: `${getConsoleBaseUrl(urlObj.origin)}/dashboard/welcome?bad_request_project=true`,
          permanent: false,
        },
      };
    }

    if (queryProjectId) {
      if (isAccessible(queryProjectId)) {
        // query로 붙은 projectId가 접근가능 시 다음 가드 진입
        return { status: STATUS_CODE.OK, projectId: queryProjectId };
      } else {
        const candidates = [recentProjectIds[0], defaultProjectId];
        const suggestedProjectId =
          candidates.find(c => isAccessible(c)) || (accessibleProjectIds[0] as string);
        // query로 붙은 projectId가 접근 불가능한 경우, 유효한 프로젝트로 변경 후 대시보드로 리다이렉트 및 프로젝트 변경 스낵바 안내
        urlObj.searchParams.set(QUERY_KEY.PROJECT_ID, suggestedProjectId);

        return {
          redirect: {
            destination: `${getConsoleBaseUrl(urlObj.origin)}/dashboard/home?${urlObj.search}&change_project=true`,
            permanent: false,
          },
        };
      }
    } else {
      if (isAccessible(recentProjectIds[0])) {
        return { status: STATUS_CODE.OK, projectId: recentProjectIds[0] };
      }

      const suggestedProjectId = isAccessible(defaultProjectId)
        ? defaultProjectId
        : (accessibleProjectIds[0] as string);

      urlObj.searchParams.set(QUERY_KEY.PROJECT_ID, suggestedProjectId);
      return {
        redirect: {
          destination: `${getConsoleBaseUrl(urlObj.origin)}/dashboard/home?${urlObj.search}&change_project=true`,
          permanent: false,
        },
      };
    }
  } catch (error) {
    return commonGuardErrorHandler({ error, context, state });
  }
};

determineProjectGuard.displayName = 'determineProjectGuard';
determineProjectGuard.traceCategory = 'authorization';
